import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Heart, ArrowLeft, MapPin, Phone, Mail, Globe, Instagram, 
  Users, Gift, DollarSign, Calendar, Handshake, Share2, Flag,
  CheckCircle, Clock, Building2, ExternalLink, ChevronRight,
  Loader2, AlertCircle, Star, Edit, Image as ImageIcon,
  MessageCircle, Video, Copy, Check,
} from 'lucide-react';
import { api } from '../services/api';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import Breadcrumbs from '../components/Breadcrumbs';
import SEOHead from '../components/SEOHead';
import OrgReviewsSection from '../components/OrgReviewsSection';
import { resolveBackendImage } from '../utils/brandLogo';


// Extrai o ID de um vídeo do YouTube — aceita youtube.com/watch, /embed/,
// /shorts/ e youtu.be/. Retorna null se URL não bater.
function extractYouTubeId(url) {
  if (!url || typeof url !== 'string') return null;
  const patterns = [
    /(?:youtube\.com\/watch\?(?:.*&)?v=)([a-zA-Z0-9_-]{6,})/i,
    /(?:youtube\.com\/embed\/)([a-zA-Z0-9_-]{6,})/i,
    /(?:youtube\.com\/shorts\/)([a-zA-Z0-9_-]{6,})/i,
    /(?:youtu\.be\/)([a-zA-Z0-9_-]{6,})/i,
  ];
  for (const p of patterns) {
    const m = url.match(p);
    if (m && m[1]) return m[1];
  }
  return null;
}


// Area labels
const AREA_LABELS = {
  'assistencia_social': 'Assistência Social',
  'cultura': 'Cultura',
  'esporte': 'Esporte',
  'educacao': 'Educação',
  'saude': 'Saúde',
  'meio_ambiente': 'Meio Ambiente',
  'causa_animal': 'Causa Animal',
  'direitos_humanos': 'Direitos Humanos',
  'empreendedorismo_social': 'Empreendedorismo Social',
  'outros': 'Outros'
};

// Entity type labels
const ENTITY_LABELS = {
  'ong': 'ONG',
  'associacao': 'Associação',
  'coletivo': 'Coletivo',
  'projeto_independente': 'Projeto Independente',
  'projeto_pessoa_fisica': 'Projeto de Pessoa Física',
  'osc': 'Organização da Sociedade Civil (OSC)',
  'oscip': 'OSCIP',
  'fundacao': 'Fundação',
  'instituto': 'Instituto',
  'outro': 'Outro'
};

// CTA Button Component
const CTAButton = ({ icon: Icon, label, color, onClick }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-3 rounded-xl font-medium text-white transition-all hover:opacity-90 ${color}`}
  >
    <Icon className="w-5 h-5" />
    {label}
  </button>
);

// Section Component
const Section = ({ title, icon: Icon, children, className = '' }) => (
  <div className={`bg-white rounded-xl border border-gray-200 p-5 ${className}`}>
    <h3 className="font-bold text-lg flex items-center gap-2 mb-4">
      <Icon className="w-5 h-5 text-purple-600" />
      {title}
    </h3>
    {children}
  </div>
);

// Activity Card Component
const ActivityCard = ({ activity, onTrack }) => {
  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  };

  return (
    <div className="border rounded-xl p-4 hover:border-purple-300 transition-all">
      <h4 className="font-bold text-gray-900">{activity.title}</h4>
      <p className="text-sm text-gray-600 mt-1 line-clamp-2">{activity.description}</p>
      
      <div className="flex flex-wrap items-center gap-3 mt-3 text-sm text-gray-500">
        {activity.start_datetime && (
          <span className="flex items-center gap-1">
            <Calendar className="w-4 h-4" />
            {formatDate(activity.start_datetime)}
          </span>
        )}
        {activity.location_text && (
          <span className="flex items-center gap-1">
            <MapPin className="w-4 h-4" />
            {activity.location_text}
          </span>
        )}
      </div>

      {activity.registration_link && (
        <a
          href={activity.registration_link}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => onTrack('registration_click', activity.id)}
          className="mt-3 inline-flex items-center gap-2 text-purple-600 hover:underline text-sm font-medium"
        >
          Inscrever-se
          <ExternalLink className="w-4 h-4" />
        </a>
      )}
    </div>
  );
};

// Supporter Card Component
const SupporterCard = ({ supporter, onTrack }) => (
  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
    {supporter.logo_url ? (
      <img 
        src={resolveBackendImage(supporter.logo_url)} 
        alt={supporter.name}
        className="w-12 h-12 rounded-lg object-cover"
      />
    ) : (
      <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center">
        <Heart className="w-6 h-6 text-purple-400" />
      </div>
    )}
    <div className="flex-1 min-w-0">
      <p className="font-medium text-sm truncate">{supporter.name}</p>
      {supporter.how_supports && (
        <p className="text-xs text-gray-500 truncate">{supporter.how_supports}</p>
      )}
    </div>
    {supporter.link && (
      <a
        href={supporter.link}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => onTrack('supporter_link_click', supporter.id)}
        className="p-2 text-gray-400 hover:text-purple-600"
      >
        <ExternalLink className="w-4 h-4" />
      </a>
    )}
  </div>
);

export default function OrganizacaoDetalhes() {
  // Duas formas de acessar:
  //   /social/:orgId                        (legado — id ou slug)
  //   /social/:municipioSlug/:orgSlug       (semântica full-path)
  //   /:municipioSlug/social/:orgId         (tenant wrapper aninhado)
  const params = useParams();
  // Discriminação clara:
  //   - Se veio `orgSlug` explícito no path → usa by-slug (path full)
  //   - Caso contrário, mesmo que exista `municipioSlug` (herdado do wrapper
  //     tenant), usamos o endpoint por id — porque o valor em `params.orgId`
  //     é justamente um id, não um slug. Antes disso era passado como slug
  //     e o backend retornava 404 (bug reportado em produção).
  const orgSlugExplicit = params.orgSlug || null;
  const orgId = orgSlugExplicit || params.orgId;
  const municipioSlug = params.municipioSlug;
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReportModal, setShowReportModal] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(null);
  const [municipio, setMunicipio] = useState(null);
  const [sharing, setSharing] = useState(false);
  const [copied, setCopied] = useState(false);

  // Check if current user is owner
  const isOwner = isAuthenticated && org && user && org.owner_id === user.id;

  const loadOrg = useCallback(async () => {
    try {
      // by-slug apenas quando temos slug EXPLÍCITO (via /:mun/:slug). Caso
      // contrário caímos em /org-projects/{id} — o id pode vir do wrapper
      // tenant aninhado (/{mun}/social/{id}), onde municipioSlug existe
      // mas o valor é um ID e não um slug.
      const endpoint = (municipioSlug && orgSlugExplicit)
        ? `/org-projects/by-slug/${municipioSlug}/${orgSlugExplicit}`
        : `/org-projects/${orgId}`;
      const res = await api.get(endpoint);
      setOrg(res.data);

      // Se o org tem slug + municipio_slug, e a URL atual não usa esse padrão,
      // redireciona para o permalink SEO (sem quebrar back button).
      const canonicalPath = res.data.municipio_slug && res.data.slug
        ? `/social/${res.data.municipio_slug}/${res.data.slug}`
        : `/social/${res.data.slug || res.data.id}`;
      if (window.location.pathname !== canonicalPath) {
        navigate(canonicalPath, { replace: true });
      }

      // Load municipality info if we have municipio_id
      if (res.data.municipio_id) {
        try {
          const munRes = await api.get('/municipios');
          const found = munRes.data.find(m => m.id === res.data.municipio_id);
          if (found) {
            setMunicipio(found);
          }
        } catch (e) {
          console.error('Error loading municipio:', e);
        }
      }

      // Track view
      trackEvent('org_profile_view', 'org_project', res.data.id);
    } catch (error) {
      console.error('Error loading org:', error);
      toast.error('Organização não encontrada');
      navigate('/social');
    } finally {
      setLoading(false);
    }
  }, [orgId, municipioSlug, orgSlugExplicit, navigate]);

  useEffect(() => {
    loadOrg();
  }, [loadOrg]);

  const trackEvent = async (eventType, entityType, entityId, ctaType = null, channel = null) => {
    try {
      await api.post('/events/track', {
        event_type: eventType,
        entity_type: entityType,
        entity_id: entityId,
        cta_type: ctaType,
        channel: channel
      });
    } catch (error) {
      console.error('Error tracking event:', error);
    }
  };

  const handleCTAClick = (ctaType, channel, link) => {
    trackEvent('cta_click', 'org_project', org.id, ctaType, channel);
    
    if (channel === 'whatsapp') {
      const phone = link.replace(/\D/g, '');
      window.open(`https://wa.me/${phone}`, '_blank');
    } else if (channel === 'email') {
      window.location.href = `mailto:${link}`;
    } else if (link) {
      window.open(link.startsWith('http') ? link : `https://${link}`, '_blank');
    }
  };

  const buildShareUrl = () => {
    // URL semântica full-path: /social/{municipio_slug}/{slug}
    const mslug = org.municipio_slug;
    const slug = org.slug || org.id;
    const path = mslug ? `/social/${mslug}/${slug}` : `/social/${slug}`;
    return `${window.location.origin}${path}`;
  };

  const buildShareText = (url) => {
    const linhas = [`💛 Conheça ${org.name}`];
    if (municipio?.nome) {
      linhas.push('');
      linhas.push(`Em ${municipio.nome}, essa iniciativa ajuda a transformar vidas.`);
    }
    const descricao = org.short_description || org.long_description || '';
    if (descricao) {
      linhas.push('');
      linhas.push(descricao.trim());
    }
    linhas.push('');
    linhas.push('Cada projeto como esse ganha ainda mais força quando chega a novas pessoas.');
    linhas.push('Conheça, compartilhe e apoie essa rede:');
    linhas.push(url);
    return linhas.join('\n');
  };

  const handleShare = async () => {
    trackEvent('share_click', 'org_project', org.id, null, 'whatsapp');
    setSharing(true);
    setTimeout(() => setSharing(false), 900); // reset da animação

    const url = buildShareUrl();
    const text = buildShareText(url);

    if (navigator.share) {
      try {
        await navigator.share({ title: org.name, text });
      } catch {
        /* user cancelled */
      }
    } else {
      const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(text)}`;
      window.open(whatsappUrl, '_blank');
    }
  };

  const handleCopyLink = async () => {
    trackEvent('copy_link', 'org_project', org.id);
    const url = buildShareUrl();
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success('Link copiado!');
      setTimeout(() => setCopied(false), 2200);
    } catch {
      toast.error('Não foi possível copiar o link.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (!org) return null;

  const supportOptions = org.support_options || {};
  const impactMetrics = org.impact_metrics || {};
  const isVerified = org.verification_status !== 'not_verified';
  const isApproved = ['aprovada', 'aprovado'].includes(org.status);

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* SEO / Open Graph — meta tags dinâmicas para preview em redes sociais */}
      <SEOHead
        title={`${org.name}${municipio?.nome ? ` — ${municipio.nome}` : ''}`}
        description={(org.short_description || org.long_description || `${org.name} no Empreende Comunidade.`).slice(0, 200)}
        canonical={`/social/${org.slug || org.id}`}
        image={org.avatar_url || (org.gallery && org.gallery[0])}
        type="article"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: org.name,
          description: org.short_description,
          url: `${window.location.origin}/social/${org.slug || org.id}`,
          logo: org.avatar_url || undefined,
          address: municipio?.nome
            ? { '@type': 'PostalAddress', addressLocality: municipio.nome, addressRegion: municipio.uf || 'MG', addressCountry: 'BR' }
            : undefined,
          sameAs: [
            org.instagram && `https://instagram.com/${(org.instagram || '').replace('@', '')}`,
            org.website,
            org.video_institucional_url,
          ].filter(Boolean),
        }}
      />

      {/* Banner de status para cadastros não aprovados */}
      {!isApproved && (
        <div className="bg-amber-500 text-white text-sm font-medium px-4 py-2.5 text-center" data-testid="org-status-banner">
          {['pendente_analise', 'pendente'].includes(org.status)
            ? 'Esta organização está em análise pela gestão municipal e ainda não foi publicada.'
            : org.status === 'necessita_ajustes'
              ? 'Este cadastro necessita de ajustes e ainda não está publicado.'
              : org.status === 'suspensa'
                ? 'Esta organização está suspensa e não aparece nas buscas públicas.'
                : 'Este cadastro não foi aprovado e não aparece nas buscas públicas.'}
        </div>
      )}

      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-500 text-white relative">
        <div className="max-w-4xl mx-auto px-4 py-6">
          {/* Breadcrumbs */}
          <Breadcrumbs 
            items={[
              { label: 'Impacto Social', path: '/social' },
              { label: org.name, path: null }
            ]}
            municipio={municipio}
            className="mb-4"
          />
          
          {/* Edit button for owner */}
          {isOwner && (
            <button
              onClick={() => navigate(`/social/${org.id}/editar`)}
              className="absolute top-6 right-4 flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-xl text-white font-medium transition-all"
              data-testid="edit-org-button"
            >
              <Edit className="w-4 h-4" />
              Editar
            </button>
          )}
          
          <div className="flex items-start gap-4">
            {/* Avatar — prefer new system imagens.logo, fallback to legacy avatar_url */}
            <div className="w-20 h-20 bg-white/20 rounded-2xl overflow-hidden flex-shrink-0">
              {(org.imagens?.logo?.medium || org.imagens?.logo?.thumb || org.avatar_url) ? (
                <img
                  src={org.imagens?.logo?.medium || org.imagens?.logo?.thumb || org.avatar_url}
                  alt={org.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Heart className="w-10 h-10 text-white/80" />
                </div>
              )}
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-2xl font-bold">{org.name}</h1>
                {isApproved && (
                  <span className="flex items-center gap-1 bg-green-400/30 border border-green-200/40 px-2 py-0.5 rounded-full text-xs font-medium" data-testid="org-selo-aprovada">
                    <CheckCircle className="w-3 h-3" />
                    {org.classificacao === 'projeto_coletivo_informal' ? 'Projeto social validado' : 'Organização verificada'}
                  </span>
                )}
                {/* Badge Verificado removido da exibição pública (iter 196). */}
              </div>
              
              <p className="text-purple-100 text-sm mt-1">
                {ENTITY_LABELS[org.entity_type] || org.entity_type}
              </p>
              
              <div className="flex items-center gap-2 mt-2 text-sm text-white/80">
                <MapPin className="w-4 h-4" />
                <span>{org.neighborhood}</span>
                {org.zone && (
                  <span className="text-white/60">• Zona {org.zone === 'urbana' ? 'Urbana' : 'Rural'}</span>
                )}
              </div>
            </div>
          </div>

          {/* Areas */}
          <div className="flex flex-wrap gap-2 mt-4">
            {(org.areas_of_action || []).map(area => (
              <span 
                key={area}
                className="bg-white/20 px-3 py-1 rounded-full text-sm"
              >
                {AREA_LABELS[area] || area}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Quick Actions */}
        <div className="flex gap-3 overflow-x-auto pb-2">
          {org.is_whatsapp ? (
            <button
              onClick={() => handleCTAClick('contact', 'whatsapp', org.whatsapp)}
              className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-full font-medium whitespace-nowrap hover:bg-green-600 transition-colors"
              data-testid="btn-quick-whatsapp"
            >
              <MessageCircle className="w-4 h-4" />
              WhatsApp
            </button>
          ) : (
            <a
              href={`tel:${(org.whatsapp || '').replace(/\D/g, '')}`}
              className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-full font-medium whitespace-nowrap hover:bg-green-600 transition-colors"
              onClick={() => trackEvent('cta_click', 'org_project', org.id, 'contact', 'phone')}
              data-testid="btn-quick-phone"
            >
              <Phone className="w-4 h-4" />
              Contato
            </a>
          )}
          
          <button
            onClick={handleShare}
            disabled={sharing}
            className={`relative flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-full font-medium whitespace-nowrap transition-all duration-200 hover:bg-purple-200 hover:scale-105 active:scale-95 overflow-hidden ${
              sharing ? 'share-btn-pulse' : ''
            }`}
            data-testid="btn-share-org"
          >
            <Share2 className={`w-4 h-4 transition-transform ${sharing ? 'share-icon-fly' : ''}`} />
            <span>Compartilhar</span>
            {sharing && (
              <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
                <span className="share-ripple" />
              </span>
            )}
          </button>

          <button
            onClick={handleCopyLink}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-purple-200 hover:border-purple-400 hover:bg-purple-50 text-purple-700 rounded-full font-medium whitespace-nowrap transition-all duration-200 active:scale-95"
            data-testid="btn-copy-link-org"
            title="Copiar link"
          >
            {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
            <span>{copied ? 'Copiado!' : 'Copiar link'}</span>
          </button>
          
          <button
            onClick={() => setShowReportModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-600 rounded-full font-medium whitespace-nowrap"
          >
            <Flag className="w-4 h-4" />
            Denunciar
          </button>
        </div>

        {/* About */}
        <Section title="Sobre" icon={Heart}>
          <p className="text-gray-700">{org.short_description}</p>
          {org.long_description && (
            <p className="text-gray-600 mt-3 text-sm">{org.long_description}</p>
          )}
        </Section>

        {/* Vídeo institucional (YouTube) */}
        {(() => {
          const videoId = extractYouTubeId(org.video_institucional_url);
          if (!videoId) return null;
          return (
            <Section title="Vídeo institucional" icon={Video}>
              <div
                className="aspect-video rounded-xl overflow-hidden bg-black"
                data-testid="video-institucional-embed"
              >
                <iframe
                  title={`Vídeo institucional — ${org.name || 'organização'}`}
                  src={`https://www.youtube.com/embed/${videoId}`}
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </Section>
          );
        })()}

        {/* How to Support */}
        {(supportOptions.donate_items?.enabled || 
          supportOptions.donate_money?.enabled || 
          supportOptions.volunteer?.enabled || 
          supportOptions.participate?.enabled || 
          supportOptions.partnership?.enabled) && (
          <Section title="Como Apoiar" icon={Heart}>
            <div className="space-y-4">
              {supportOptions.donate_items?.enabled && (
                <div className="p-4 bg-orange-50 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <Gift className="w-5 h-5 text-orange-500" />
                    <h4 className="font-medium">Doar Itens</h4>
                  </div>
                  {supportOptions.donate_items.items_accepted && (
                    <p className="text-sm text-gray-600 mb-2">
                      <strong>Aceita:</strong> {supportOptions.donate_items.items_accepted}
                    </p>
                  )}
                  {supportOptions.donate_items.instructions && (
                    <p className="text-sm text-gray-600">{supportOptions.donate_items.instructions}</p>
                  )}
                  <button
                    onClick={() => handleCTAClick('donate_items', 'whatsapp', org.whatsapp)}
                    className="mt-3 flex items-center gap-2 text-orange-600 font-medium text-sm hover:underline"
                  >
                    <Phone className="w-4 h-4" />
                    Entrar em contato
                  </button>
                </div>
              )}

              {supportOptions.donate_money?.enabled && (
                <div className="p-4 bg-green-50 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign className="w-5 h-5 text-green-500" />
                    <h4 className="font-medium">Doar em Dinheiro</h4>
                  </div>
                  {!isVerified && (
                    <div className="flex items-start gap-2 p-2 bg-yellow-100 rounded-lg mb-2">
                      <AlertCircle className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-yellow-800">
                        Esta organização ainda não foi verificada. Confirme a autenticidade antes de doar.
                      </p>
                    </div>
                  )}
                  {supportOptions.donate_money.instructions && (
                    <p className="text-sm text-gray-600">{supportOptions.donate_money.instructions}</p>
                  )}
                  {supportOptions.donate_money.link && (
                    <a
                      href={supportOptions.donate_money.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => trackEvent('cta_click', 'org_project', org.id, 'donate_money', 'site')}
                      className="mt-3 inline-flex items-center gap-2 text-green-600 font-medium text-sm hover:underline"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Página de doação
                    </a>
                  )}
                </div>
              )}

              {supportOptions.volunteer?.enabled && (
                <div className="p-4 bg-blue-50 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="w-5 h-5 text-blue-500" />
                    <h4 className="font-medium">Ser Voluntário</h4>
                  </div>
                  {supportOptions.volunteer.instructions && (
                    <p className="text-sm text-gray-600">{supportOptions.volunteer.instructions}</p>
                  )}
                  <button
                    onClick={() => handleCTAClick('volunteer', 'whatsapp', org.whatsapp)}
                    className="mt-3 flex items-center gap-2 text-blue-600 font-medium text-sm hover:underline"
                  >
                    <Phone className="w-4 h-4" />
                    Quero ser voluntário
                  </button>
                </div>
              )}

              {supportOptions.participate?.enabled && (
                <div className="p-4 bg-purple-50 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar className="w-5 h-5 text-purple-500" />
                    <h4 className="font-medium">Participar</h4>
                  </div>
                  {supportOptions.participate.instructions && (
                    <p className="text-sm text-gray-600">{supportOptions.participate.instructions}</p>
                  )}
                  {supportOptions.participate.link && (
                    <a
                      href={supportOptions.participate.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => trackEvent('cta_click', 'org_project', org.id, 'participate', 'site')}
                      className="mt-3 inline-flex items-center gap-2 text-purple-600 font-medium text-sm hover:underline"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Inscrever-se
                    </a>
                  )}
                </div>
              )}

              {supportOptions.partnership?.enabled && (
                <div className="p-4 bg-teal-50 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <Handshake className="w-5 h-5 text-teal-500" />
                    <h4 className="font-medium">Parcerias</h4>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {supportOptions.partnership.contact_whatsapp && (
                      <button
                        onClick={() => handleCTAClick('partnership', 'whatsapp', supportOptions.partnership.contact_whatsapp)}
                        className="flex items-center gap-2 px-3 py-1.5 bg-teal-500 text-white rounded-lg text-sm"
                      >
                        <Phone className="w-4 h-4" />
                        WhatsApp
                      </button>
                    )}
                    {supportOptions.partnership.contact_email && (
                      <button
                        onClick={() => handleCTAClick('partnership', 'email', supportOptions.partnership.contact_email)}
                        className="flex items-center gap-2 px-3 py-1.5 bg-teal-100 text-teal-700 rounded-lg text-sm"
                      >
                        <Mail className="w-4 h-4" />
                        E-mail
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </Section>
        )}

        {/* Próximas ações (activities filtrados no backend: start_datetime >= hoje) */}
        {org.activities && org.activities.length > 0 && (
          <Section title="Próximas ações" icon={Calendar}>
            <div className="space-y-3" data-testid="proximas-acoes-list">
              {org.activities.map(activity => (
                <ActivityCard 
                  key={activity.id} 
                  activity={activity}
                  onTrack={trackEvent}
                />
              ))}
            </div>
          </Section>
        )}

        {/* Impact */}
        {(impactMetrics.people_impacted_last_month || impactMetrics.people_impacted_last_year) && (
          <Section title="Impacto" icon={Users}>
            <div className="p-3 bg-purple-50 rounded-xl mb-4">
              <p className="text-xs text-purple-600 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                Dados autodeclarados pela organização
              </p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              {impactMetrics.people_impacted_last_month && (
                <div className="text-center p-4 bg-gray-50 rounded-xl">
                  <p className="text-2xl font-bold text-purple-600">
                    {impactMetrics.people_impacted_last_month}
                  </p>
                  <p className="text-sm text-gray-600">pessoas/mês</p>
                </div>
              )}
              {impactMetrics.people_impacted_last_year && (
                <div className="text-center p-4 bg-gray-50 rounded-xl">
                  <p className="text-2xl font-bold text-purple-600">
                    {impactMetrics.people_impacted_last_year}
                  </p>
                  <p className="text-sm text-gray-600">pessoas/ano</p>
                </div>
              )}
            </div>
            
            {impactMetrics.measurement_method && (
              <p className="text-sm text-gray-500 mt-3">
                <strong>Como medem:</strong> {impactMetrics.measurement_method}
              </p>
            )}
          </Section>
        )}

        {/* Depoimentos + Corações (iter282) */}
        <Section title="Depoimentos" icon={Heart}>
          <OrgReviewsSection
            orgId={org.id}
            currentUser={isAuthenticated ? user : null}
            isOwner={isOwner}
          />
        </Section>

        {/* Parceiros (supporters filtered by supporter_type='empresa') */}
        {(() => {
          const parceiros = (org.supporters || []).filter(
            (s) => s.supporter_type === 'empresa',
          );
          if (parceiros.length === 0) return null;
          return (
            <Section title={`Parceiros (${parceiros.length})`} icon={Handshake}>
              <div
                className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3"
                data-testid="parceiros-grid"
              >
                {parceiros.map((p) => (
                  <a
                    key={p.id}
                    href={p.link || undefined}
                    target={p.link ? '_blank' : undefined}
                    rel={p.link ? 'noopener noreferrer' : undefined}
                    onClick={() => p.link && trackEvent('supporter_link_click', 'supporter', p.id)}
                    className="group flex flex-col items-center gap-1.5"
                    data-testid={`parceiro-${p.id}`}
                    title={p.name}
                  >
                    <div className="w-full aspect-square bg-white rounded-xl border border-gray-200 overflow-hidden flex items-center justify-center p-2 group-hover:border-purple-300 transition-colors">
                      {p.logo_url ? (
                        <img
                          src={resolveBackendImage(p.logo_url)}
                          alt={p.name}
                          className="w-full h-full object-contain"
                          loading="lazy"
                        />
                      ) : (
                        <span className="text-xs text-gray-500 text-center">{p.name}</span>
                      )}
                    </div>
                    <span className="text-xs text-gray-700 text-center leading-tight w-full truncate">
                      {p.name}
                    </span>
                  </a>
                ))}
              </div>
            </Section>
          );
        })()}

        {/* Supporters (não-empresa: pessoa, instituicao, poder_publico, outro) */}
        {(() => {
          const apoiadores = (org.supporters || []).filter(
            (s) => s.supporter_type !== 'empresa',
          );
          if (apoiadores.length === 0) return null;
          return (
            <Section title={`Apoiadores (${apoiadores.length})`} icon={Heart}>
              <div className="space-y-2">
                {apoiadores.map((supporter) => (
                  <SupporterCard
                    key={supporter.id}
                    supporter={supporter}
                    onTrack={trackEvent}
                  />
                ))}
              </div>
            </Section>
          );
        })()}

        {/* Gallery — merge new system imagens.galeria + legacy gallery URLs */}
        {(() => {
          const galleryUrls = [
            ...((org.imagens?.galeria || [])
              .map((img) => img.medium || img.large || img.thumb)
              .filter(Boolean)),
            ...((org.gallery || []).filter((u) => typeof u === 'string' && u)),
          ];
          if (galleryUrls.length === 0) return null;
          return (
            <Section title={`Galeria (${galleryUrls.length})`} icon={ImageIcon}>
              <div className="grid grid-cols-3 gap-2">
                {galleryUrls.map((url, idx) => (
                  <button
                    key={url || `gal-${idx}`}
                    onClick={() => setGalleryIndex(idx)}
                    className="aspect-square rounded-xl overflow-hidden hover:opacity-90 transition-opacity"
                  >
                    <img src={url} alt={`Foto ${idx + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            </Section>
          );
        })()}

        {/* Contact */}
        <Section title="Contato" icon={Phone}>
          <div className="space-y-3">
            <a
              href={`https://wa.me/${org.whatsapp?.replace(/\D/g, '')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-3 bg-green-50 rounded-xl hover:bg-green-100 transition-colors"
            >
              <Phone className="w-5 h-5 text-green-600" />
              <span className="text-green-700">{org.whatsapp}</span>
            </a>
            
            {org.email && (
              <a
                href={`mailto:${org.email}`}
                className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
              >
                <Mail className="w-5 h-5 text-gray-600" />
                <span className="text-gray-700">{org.email}</span>
              </a>
            )}
            
            {org.instagram && (
              <a
                href={`https://instagram.com/${org.instagram.replace('@', '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-3 bg-pink-50 rounded-xl hover:bg-pink-100 transition-colors"
              >
                <Instagram className="w-5 h-5 text-pink-600" />
                <span className="text-pink-700">{org.instagram}</span>
              </a>
            )}
            
            {org.website && (
              <a
                href={org.website.startsWith('http') ? org.website : `https://${org.website}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-3 bg-blue-50 rounded-xl hover:bg-blue-100 transition-colors"
              >
                <Globe className="w-5 h-5 text-blue-600" />
                <span className="text-blue-700 truncate">{org.website}</span>
              </a>
            )}
          </div>
        </Section>
      </div>

      {/* Gallery Modal */}
      {(() => {
        const galleryUrls = [
          ...((org.imagens?.galeria || [])
            .map((img) => img.medium || img.large || img.thumb)
            .filter(Boolean)),
          ...((org.gallery || []).filter((u) => typeof u === 'string' && u)),
        ];
        if (galleryIndex === null || galleryUrls.length === 0) return null;
        return (
          <div
            className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center"
            onClick={() => setGalleryIndex(null)}
          >
            <button
              onClick={() => setGalleryIndex(null)}
              className="absolute top-4 right-4 p-2 text-white/80 hover:text-white"
            >
              <span className="sr-only">Fechar</span>
              ✕
            </button>
            <img
              src={galleryUrls[galleryIndex]}
              alt={`Foto ${galleryIndex + 1}`}
              className="max-h-[90vh] max-w-[90vw] object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            {galleryUrls.length > 1 && (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                {galleryUrls.map((url, idx) => (
                  <button
                    key={`dot-${url || idx}`}
                    onClick={(e) => { e.stopPropagation(); setGalleryIndex(idx); }}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      idx === galleryIndex ? 'bg-white' : 'bg-white/40'
                    }`}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}
